import {Autocomplete, TextField, TextFieldProps} from '@mui/material';
import isNil from 'lodash/isNil';
import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import reduxSelectors from '../../redux/selectors';

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'name'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'sx'
> & {
  value?: number | null;
  nullLabel?: string;
  forceDefault?: boolean;
  onChange?: (value?: number | null) => void;
};

export const ShiftSelect = ({
  value,
  disabled,
  fullWidth,
  size,
  nullLabel,
  forceDefault,
  onChange,
  ...props
}: Props) => {
  const assets = useSelector(reduxSelectors.assets.getAssets);
  const nullValue = -1;

  const options = useMemo(() => {
    const activeShifts = assets.shifts.filter((i) => i.status === 'active');
    if (nullLabel) {
      return [
        {
          id: nullValue,
          name: nullLabel,
        },
        ...activeShifts,
      ];
    }
    return activeShifts;
  }, [assets.shifts, nullLabel]);

  const defaultOptionId = useMemo(() => {
    const id = options.find((i) => i.name === 'Day')?.id ?? options[0]?.id;
    return id;
  }, [options]);

  const selectedOptionId = isNil(value)
    ? forceDefault
      ? defaultOptionId
      : nullValue
    : value;
  const selectedOption = options.find((i) => i.id === selectedOptionId) ?? null;

  useEffect(() => {
    if (isNil(value) && forceDefault && defaultOptionId !== null) {
      onChange?.(defaultOptionId);
    }
  }, [value, defaultOptionId]);

  return disabled ? (
    <TextField
      {...props}
      value={options.find((i) => i.id === value)?.name ?? value ?? ''}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
    />
  ) : (
    <Autocomplete
      value={selectedOption ?? null}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      size={size}
      disableClearable={selectedOptionId === nullValue}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.name ?? ''}
      renderInput={(params) => (
        <TextField label="Shift" {...props} {...params} />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        );
      }}
      onChange={(_, option) => {
        const v =
          !option || isNil(option.id) || option.id === nullValue
            ? null
            : option.id;
        onChange?.(v);
      }}
    />
  );
};
